import { useEffect, useCallback, useMemo } from 'react';
import { filterCollectionForSearch } from '../../utils/collectionFolders';
import { loadSidebarState, saveSidebarState } from './sidebarState';

/** Collapsed section, expanded collections / folders per workspace, and search filtering. */
export function useSidebarExpansion({
  collections,
  workspaceId,
  editingItem,
  collectionsCollapsedKey,
  setCollectionsCollapsed,
  expandedCollections,
  setExpandedCollections,
  expandedFolders,
  setExpandedFolders,
  expandedForWorkspaceRef,
  prevCollectionIdsRef,
  preSearchStateRef,
  searchTerm,
}) {
  // ── Section collapse (Collections) ─────────────────────────────────────────

  useEffect(() => {
    if (!collectionsCollapsedKey) return;
    try {
      const raw = localStorage.getItem(collectionsCollapsedKey);
      if (raw != null) setCollectionsCollapsed(raw === '1');
    } catch {
      /* ignore corrupt state */
    }
  }, [collectionsCollapsedKey, setCollectionsCollapsed]);

  const toggleCollectionsSection = useCallback(() => {
    setCollectionsCollapsed((prev) => {
      const next = !prev;
      if (collectionsCollapsedKey) {
        try {
          localStorage.setItem(collectionsCollapsedKey, next ? '1' : '0');
        } catch {
          /* ignore quota errors */
        }
      }
      return next;
    });
  }, [collectionsCollapsedKey, setCollectionsCollapsed]);

  // ── Expansion state management ─────────────────────────────────────────────

  useEffect(() => {
    if (editingItem?.type !== 'request') return;
    const parent = collections.find((c) =>
      (c.requests || []).some((r) => String(r.id) === String(editingItem.id))
    );
    if (!parent) return;
    setExpandedCollections((prev) => {
      if (prev.has(parent.id)) return prev;
      const next = new Set(prev);
      next.add(parent.id);
      return next;
    });
  }, [editingItem, collections, setExpandedCollections]);

  useEffect(() => {
    const ids   = collections.map((c) => c.id);
    const idSet = new Set(ids);

    if (collections.length === 0) {
      expandedForWorkspaceRef.current = null;
      prevCollectionIdsRef.current    = new Set();
      preSearchStateRef.current       = null;
      setExpandedCollections(new Set());
      setExpandedFolders(new Set());
      return;
    }

    if (expandedForWorkspaceRef.current !== workspaceId) {
      expandedForWorkspaceRef.current = workspaceId;
      prevCollectionIdsRef.current    = idSet;
      const saved = loadSidebarState(workspaceId);
      if (saved) {
        setExpandedCollections(new Set(saved.expandedCollections || []));
        setExpandedFolders(new Set(saved.expandedFolders || []));
      } else {
        setExpandedCollections(new Set(ids));
        setExpandedFolders(new Set());
      }
      return;
    }

    const prevSet   = prevCollectionIdsRef.current || new Set();
    const newColIds = ids.filter((id) => !prevSet.has(id));

    setExpandedCollections((expanded) => {
      const next = new Set(expanded);
      let changed = false;
      for (const id of [...next]) {
        if (!idSet.has(id)) { next.delete(id); changed = true; }
      }
      for (const id of newColIds) { next.add(id); changed = true; }
      return changed ? next : expanded;
    });

    if (newColIds.length > 0) {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        for (const id of newColIds) {
          const col = collections.find((c) => String(c.id) === String(id));
          for (const f of (col?.settings?.folders || [])) next.add(f.id);
        }
        return next;
      });
    }

    prevCollectionIdsRef.current = idSet;
  }, [workspaceId, collections, expandedForWorkspaceRef, preSearchStateRef, prevCollectionIdsRef, setExpandedCollections, setExpandedFolders]);

  useEffect(() => {
    if (!workspaceId || expandedForWorkspaceRef.current !== workspaceId) return;
    const timer = setTimeout(() => {
      saveSidebarState(workspaceId, expandedCollections, expandedFolders);
    }, 800);
    return () => clearTimeout(timer);
  }, [workspaceId, expandedCollections, expandedFolders, expandedForWorkspaceRef]);

  // ── Search ─────────────────────────────────────────────────────────────────

  const filteredCollections = useMemo(() => {
    const q = searchTerm.trim();
    if (!q) return collections;
    const ql = q.toLowerCase();
    // Hide collections with no matches; a collection whose own name matches
    // stays visible even when it has no (matching) requests or folders.
    return collections
      .map((c) => filterCollectionForSearch(c, q))
      .filter(
        (c) =>
          c.name?.toLowerCase().includes(ql) ||
          (c.requests?.length || 0) > 0 ||
          (c.settings?.folders?.length || 0) > 0
      );
  }, [collections, searchTerm]);

  useEffect(() => {
    const q = searchTerm.trim();
    if (q && !preSearchStateRef.current) {
      preSearchStateRef.current = { expandedCollections, expandedFolders };
    } else if (!q && preSearchStateRef.current) {
      const { expandedCollections: savedCols, expandedFolders: savedFolders } =
        preSearchStateRef.current;
      preSearchStateRef.current = null;
      setExpandedCollections(savedCols);
      setExpandedFolders(savedFolders);
    }
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchTerm.trim()) return;
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const col of filteredCollections) {
        const hasContent =
          (col.requests?.length || 0) > 0 || (col.settings?.folders?.length || 0) > 0;
        if (hasContent && !next.has(col.id)) { next.add(col.id); changed = true; }
      }
      return changed ? next : prev;
    });
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const col of filteredCollections) {
        for (const f of (col.settings?.folders || [])) {
          if (!next.has(f.id)) { next.add(f.id); changed = true; }
        }
      }
      return changed ? next : prev;
    });
  }, [searchTerm, filteredCollections, setExpandedCollections, setExpandedFolders]);

  return { toggleCollectionsSection, filteredCollections };
}
